import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, CalendarX, Wallet, CheckCircle2, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const EmployeeLedger = () => {
    const [page, setPage] = useState(1);
    const axiosSecure = UseAxiosSecure();
    const { activeBranchId } = UseAuth();

    // FETCH LEDGER DATA
    const { data: queryData, isLoading } = useQuery({
        queryKey: ['employee-ledger', activeBranchId, page],
        queryFn: async () => {
            const res = await axiosSecure.get('/hr/ledger', {
                params: { page, limit: 10 }
            });
            return res.data?.data || {};
        },
        enabled: !!activeBranchId,
        keepPreviousData: true
    });

    const ledger = queryData?.ledger || [];
    const totalPages = queryData?.totalPages || 1;
    const totalEmployees = queryData?.totalEmployees || 0;

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Ledger...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* --- HEADER --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <BookOpen className="text-cyan-500" /> Employee Ledger
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Master report of leaves, absences, and pending salary across {totalEmployees} employees.</p>
                </div>
            </div>

            {/* --- TABLE --- */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl relative min-h-[400px]">
                {isLoading && (
                    <div className="absolute inset-0 bg-[#1a103c]/80 z-20 flex items-center justify-center">
                        <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-slate-400 bg-white/5">
                                <th className="p-6 font-semibold">Employee Details</th>
                                <th className="p-6 font-semibold text-center">Total Approved Leaves</th>
                                <th className="p-6 font-semibold text-center text-orange-400">Unpaid Absences</th>
                                <th className="p-6 font-semibold text-right text-emerald-400">Total Salary Due</th>
                            </tr>
                        </thead>

                        <tbody className="text-sm">
                            {ledger.length === 0 && !isLoading && (
                                <tr>
                                    <td colSpan="4" className="p-10 text-center text-slate-500">
                                        <FileText size={48} className="mx-auto mb-3 opacity-20" />
                                        <p>No employee records found for the ledger.</p>
                                    </td>
                                </tr>
                            )}
                            {ledger.map((record) => (
                                <tr key={record._id} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-none">
                                    <td className="p-6">
                                        <div className="flex items-center gap-3">
                                            <img 
                                                src={record.user?.profileImage || `https://ui-avatars.com/api/?name=${record.user?.name}&background=random`} 
                                                alt="Profile" 
                                                className="w-12 h-12 rounded-xl object-cover border-2 border-white/10"
                                            />
                                            <div>
                                                <h3 className="font-bold text-white text-base">{record.user?.name || 'Unknown'}</h3>
                                                <div className="text-slate-400 text-xs flex items-center gap-2 mt-1">
                                                    <span>{record.jobTitle}</span>
                                                    <span className="w-1 h-1 bg-slate-500 rounded-full"></span>
                                                    <span>{record.salaryType}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    
                                    <td className="p-6 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <span className="text-xl font-black text-white">{record.totalLeaves}</span>
                                            <span className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1 mt-1">
                                                <CheckCircle2 size={10} className="text-emerald-500"/> Days Off
                                            </span>
                                        </div>
                                    </td>
                                    
                                    <td className="p-6 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <span className={`text-xl font-black ${record.unpaidAbsences > 0 ? 'text-orange-400' : 'text-slate-500'}`}>
                                                {record.unpaidAbsences}
                                            </span>
                                            <span className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1 mt-1">
                                                <CalendarX size={10} className={record.unpaidAbsences > 0 ? 'text-orange-400' : 'text-slate-600'}/> Unpaid
                                            </span>
                                        </div>
                                    </td>

                                    <td className="p-6 text-right">
                                        <div className="flex flex-col items-end justify-center">
                                            <span className={`text-2xl font-black font-mono tracking-tight ${record.salaryDue > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                                                ${record.salaryDue.toFixed(2)}
                                            </span>
                                            <span className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1 mt-1">
                                                <Wallet size={10} className={record.salaryDue > 0 ? 'text-emerald-500' : 'text-slate-600'}/> Pending Pay
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* --- PAGINATION CONTROLS --- */}
                    <div className="p-4 flex justify-end items-center gap-4 border-t border-white/10 bg-black/10">
                        <span className="text-sm text-slate-400">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <button 
                                disabled={page === 1} 
                                onClick={() => setPage(p => p - 1)} 
                                className="p-2 bg-white/5 rounded-lg disabled:opacity-30 hover:bg-white/10 text-white transition-colors"
                            >
                                <ChevronLeft size={18}/>
                            </button>
                            <button 
                                disabled={page >= totalPages} 
                                onClick={() => setPage(p => p + 1)} 
                                className="p-2 bg-white/5 rounded-lg disabled:opacity-30 hover:bg-white/10 text-white transition-colors"
                            >
                                <ChevronRight size={18}/>
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default EmployeeLedger;